import { Box, Dialog, DialogContent, Typography } from '@material-ui/core';
import { startOfISOWeek } from 'date-fns/esm';
import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { FilterDate } from '../../types/commonForm';
import { Coupon, CouponTypes } from '../../types/coupons';
import { CouponCreateModal } from '../common/couponsShared/couponsModals/couponCreateModal';
import { CouponViewModal } from '../common/couponsShared/couponsModals/couponModalView';
import { AppButton } from '../common/ctaButton/button';
import { RemoveEntityModalContent } from '../common/removeEntityPopUp/RemoveEntity';
import { Charity } from '../../types/charities';
import { CharitiesTable } from '../common/charitiesShared/charitiesTable/charitiesTable';
import { CharityViewModal } from '../common/charitiesShared/charitiesModals/charityModalView';
import { CharityCreateModal } from '../common/charitiesShared/charitiesModals/charityCreateModal';
import { ChangeEntityModalContent } from '../common/charitiesShared/changeCharityCheckoutPopup/ChangeEntityModalContent';

interface NewState {
  charities: Array<Charity>;
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
  searchField: string;
  searchValue: string;
  typeFilter: Array<string>;
  statusFilter: Array<string>;
  startDate: FilterDate;
  endDate: FilterDate;
}

interface State {
  coupons: Array<Coupon>;
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
  searchField: string;
  searchValue: string;
  typeFilter: Array<string>;
  statusFilter: Array<string>;
  startDate: FilterDate;
  endDate: FilterDate;
}

export const GeneralCharitiesTab = () => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [state, setState] = useState<NewState>({
    charities: Array<Charity>(),
    total: 0,
    page: 0,
    perPage: 10,
    field: '_id',
    order: 'desc',
    searchField: 'couponId',
    searchValue: '',
    typeFilter: Array<string>(),
    statusFilter: Array<string>(),
    startDate: {
      startDate: undefined,
      endDate: undefined,
    },
    endDate: {
      startDate: undefined,
      endDate: undefined,
    },
  });

  const [currentCharity, setCurrentCharity] = useState<Charity>();
  const [modalsState, setModals] = useState({
    viewCharity: false,
    createCharity: false,
    editCharity: false,
    deleteCharity: false,
    checkoutHide: false,
  });

  const [typeFilter, setTypeFilter] = useState({
    [CouponTypes.Basket]: false,
    [CouponTypes.Credit]: false,
    [CouponTypes.Prize]: false,
    [CouponTypes.FixedOdds]: false,
    [CouponTypes.DreamHome]: false,
  });

  const [statusFilter, setStatusFilter] = useState({
    Active: false,
    Inactive: false,
    Used: false,
    Pending: false,
  });

  useEffect(() => {
    getCharities(
      1,
      10,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      Array<string>(),
      Array<string>(),
      state.startDate,
      state.endDate
    );
  }, []);
  const getCharities = async (
    page: number,
    perPage: number,
    field: string,
    order: string,
    searchField: string,
    searchValue: string,
    typeFilter: Array<string>,
    statusFilter: Array<string>,
    startDate: FilterDate,
    endDate: FilterDate
  ): Promise<void> => {
    const resource = '/charities/get';
    const result = (await dataProvider.getCharities(resource, {
      pagination: { page, perPage },
      sort: { field, order },
      search: { field: searchField, value: searchValue },
      filter: {
        typeFilter: typeFilter,
        statusFilter: statusFilter,
        startDate: startDate,
        endDate: endDate,
      },
    })) as any;
    setState({
      ...state,
      charities: result.data.charities,
      total: result.data.allCount,
      page,
      perPage,
      field,
      order,
      searchField,
      searchValue,
      statusFilter,
      typeFilter,
    });
  };

  const handleFirstPage = () => {
    getCharities(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getCharities(
      page,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getCharities(
      newPage + 1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getCharities(
      1,
      perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const handleChangeCharityStatus = async (
    charityId: string | undefined,
    type: string
  ) => {
    const charity = state.charities.find((i) => i._id === charityId);
    const charityIndex = state.charities.findIndex((i) => i._id === charityId);

    if (charity) {
      try {
        const types = {
          'checkout-page': 'isActiveOnCheckoutPage',
          'charity-page': 'isActiveOnCharityPage',
        };
        const name = types[type];
        const res = await dataProvider.changeCharityStatus(
          '/charities/status',
          {
            _id: state.charities[charityIndex]._id,
            //   isActive: !state.charities[charityIndex].isActive,
            paramName: name,
            [name]: !state.charities[charityIndex][name],
            type: type,
          }
        ) as any;
        if (res.data === 200) {
          setState({
            ...state,
            charities: [
              ...state.charities.slice(0, charityIndex),
              {
                ...state.charities[charityIndex],
                [name]: !state.charities[charityIndex][name],
              },
              ...state.charities.slice(charityIndex + 1),
            ],
          });

          getCharities(
            state.page,
            state.perPage,
            state.field,
            state.order,
            state.searchField,
            state.searchValue,
            state.typeFilter,
            state.statusFilter,
            state.startDate,
            state.endDate
          );
        }
      } catch (err) {
        notify(`${err} `, 'error');
      }
      handleCloseModals('checkoutHide')();
    }
  };
  const handleShowChangeStatusPopup = (charity: Charity) => {
    setModals((state) => ({
      ...state,
      checkoutHide: true,
    }));
    setCurrentCharity(charity);
    // if (!charity.isActiveOnCheckoutPage) {
    //   return;
    // }
  };
  const handleChangeCharityCheckoutStatus = async (
    charityId: string | undefined,
    type: string
  ) => {
    const charity = state.charities.find((i) => i._id === charityId);
    const charityIndex = state.charities.findIndex((i) => i._id === charityId);

    if (charity) {
      try {
        if (!charity.isActiveOnCheckoutPage) {
          setModals((state) => ({
            ...state,
            checkoutHide: true,
          }));
          return;
        }
        const types = {
          'checkout-page': 'isActiveOnCheckoutPage',
          'charity-page': 'isActiveOnCharityPage',
        };
        const name = types[type];
        const res = await dataProvider.changeCharityStatus(
          '/charities/status',
          {
            _id: state.charities[charityIndex]._id,
            //   isActive: !state.charities[charityIndex].isActive,
            paramName: name,
            [name]: !state.charities[charityIndex][name],
            type: type,
          }
        ) as any;
        if (res.data === 200) {
          setState({
            ...state,
            charities: [
              ...state.charities.slice(0, charityIndex),
              {
                ...state.charities[charityIndex],
                [name]: !state.charities[charityIndex][name],
              },
              ...state.charities.slice(charityIndex + 1),
            ],
          });

          getCharities(
            state.page,
            state.perPage,
            state.field,
            state.order,
            state.searchField,
            state.searchValue,
            state.typeFilter,
            state.statusFilter,
            state.startDate,
            state.endDate
          );
        }
      } catch (err) {
        notify(`${err} `, 'error');
      }
    }
  };
  const handleCloseModals =
    (
      modalName:
        | 'viewCharity'
        | 'editCharity'
        | 'createCharity'
        | 'deleteCharity'
        | 'checkoutHide'
    ) =>
    () => {
      setModals((state) => ({
        ...state,
        [modalName]: false,
      }));
    };
  const handleShowModals = (
    modalName:
      | 'viewCharity'
      | 'editCharity'
      | 'createCharity'
      | 'deleteCharity'
      | 'checkoutHide'
  ): (() => void) => {
    if (
      modalName === 'viewCharity' ||
      modalName === 'editCharity' ||
      modalName === 'deleteCharity' ||
      modalName === 'checkoutHide'
    ) {
      return (charityId?: string) => {
        const charity = state.charities.find((i) => i._id === charityId);
        setCurrentCharity(charity);
        setModals((state) => ({
          ...state,
          [modalName]: true,
        }));
      };
    }

    return () => {
      setModals((state) => ({
        ...state,
        [modalName]: true,
      }));
    };
  };

  const handleDeleteCoupon = (couponId: string) => async () => {
    try {
      const result = await dataProvider.delete('coupons', {
        id: couponId,
        previousData: {} as any,
      });
      if (result.data) {
        notify(result.data.message, 'info');
        getCharities(
          state.page,
          state.perPage,
          state.field,
          state.order,
          state.searchField,
          state.searchValue,
          state.typeFilter,
          state.statusFilter,
          state.startDate,
          state.endDate
        );
        setModals({
          ...modalsState,
          deleteCharity: false,
        });
      }
    } catch (error) {
      notify('Charity not found', 'error');
    }
  };

  const handleSort = (direction: string, field: string) => {
    getCharities(
      state.page,
      state.perPage,
      field,
      direction,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const applySearch = (field: string) => {
    getCharities(
      1,
      state.perPage,
      state.field,
      state.order,
      field,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const checkTypesFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const filter = typeFilter;
    filter[name] = event.target.checked;
    setState({
      ...state,
      typeFilter: Object.keys(filter).filter((status) => filter[status]),
    });
    setTypeFilter({ ...typeFilter, [name]: event.target.checked });
  };

  const checkStatusFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const filter = statusFilter;
    filter[name] = event.target.checked;
    setState({
      ...state,
      statusFilter: Object.keys(filter).filter((status) => filter[status]),
    });
    setStatusFilter({ ...statusFilter, [name]: event.target.checked });
  };

  const applyStatusFilter = () => {
    const filter = Object.keys(statusFilter).filter(
      (status) => statusFilter[status]
    );
    getCharities(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      filter,
      state.startDate,
      state.endDate
    );
  };

  const applyTypeFilter = () => {
    const filter = Object.keys(typeFilter).filter((type) => typeFilter[type]);
    getCharities(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      filter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const clearTypeFilter = () => {
    setTypeFilter({
      ...typeFilter,
      BASKET: false,
      CREDIT: false,
      PRIZE: false,
      FIXED_ODDS: false,
      DREAMHOME: false,
    });
    setState({ ...state, typeFilter: Array<string>() });
  };

  const clearStatusFilter = () => {
    setStatusFilter({
      ...statusFilter,
      Active: false,
      Inactive: false,
      Used: false,
    });
    setState({ ...state, statusFilter: Array<string>() });
  };

  const setStartStartDate = (value: Date) => {
    const startDateObject = state.startDate;
    startDateObject.startDate = value;
    setState({ ...state, startDate: startDateObject });
  };

  const setStartEndDate = (value: Date) => {
    const startDate = state.startDate;
    startDate.endDate = value;
    setState({ ...state, startDate: startDate });
  };

  const clearStartDateFilter = () => {
    const startDateObject = { startDate: undefined, endDate: undefined };
    setState({ ...state, startDate: startDateObject });
  };

  const clearEndDateFilter = () => {
    const endDateObject = { startDate: undefined, endDate: undefined };
    setState({ ...state, endDate: endDateObject });
  };

  const setEndStartDate = (value: Date) => {
    const endDateObject = state.endDate;
    endDateObject.startDate = value;
    setState({ ...state, endDate: endDateObject });
  };

  const setEndEndDate = (value: Date) => {
    const endDateObject = state.endDate;
    endDateObject.endDate = value;
    setState({ ...state, endDate: endDateObject });
  };

  const applyDateFilter = () => {
    getCharities(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.typeFilter,
      state.statusFilter,
      state.startDate,
      state.endDate
    );
  };

  const search = (value: string, field: string) => {
    setState({ ...state, searchField: field, searchValue: value });
  };

  const clearSearch = (field: string) => {
    setState({ ...state, searchValue: '' });
  };

  const sortCharityRandomly = async () => {
    try {
      const { data } = (await dataProvider.sortCharityRandomly(
        '/charities/sort'
      )) as { data: { ok: number } };
      if (data.ok) notify('Charity sorted successfully', 'info');
    } catch (err) {
      notify(`${err} `, 'error');
    }
  };

  return (
    <Box className="couponsTabWrapper">
      <div className="addCupponRow">
        <AppButton
          onClick={() => sortCharityRandomly()}
          text="Sort Charity Randomly"
        />
        <AppButton
          onClick={handleShowModals('createCharity')}
          text="Add Charity"
        />
      </div>
      <CharitiesTable
        charities={state.charities}
        tableComponentScope={'general'}
        page={state.page}
        perPage={state.perPage}
        collectionLength={state.charities.length}
        total={state.total}
        isView={false}
        handleShowViewModal={handleShowModals('viewCharity')}
        handleShowEditModal={handleShowModals('editCharity')}
        handleShowDeleteModal={handleShowModals('deleteCharity')}
        handleShowChangeStatusPopup={handleShowChangeStatusPopup}
        handleChangeCharityStatus={handleChangeCharityStatus}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleFirstPage={handleFirstPage}
        handleLastPage={handleLastPage}
        handleSort={handleSort}
        applySearch={applySearch}
        typeFilterValues={typeFilter}
        statusFilterValues={statusFilter}
        checkTypesFilter={checkTypesFilter}
        checkStatusFilter={checkStatusFilter}
        applyTypeFilter={applyTypeFilter}
        applyStatusFilter={applyStatusFilter}
        clearTypeFilter={clearTypeFilter}
        clearStatusFilter={clearStatusFilter}
        startDate={state.startDate}
        finishDate={state.endDate}
        setStartStartDate={setStartStartDate}
        setStartEndDate={setStartEndDate}
        clearStartDateFilter={clearStartDateFilter}
        applyDateFilter={applyDateFilter}
        setEndStartDate={setEndStartDate}
        setEndEndDate={setEndEndDate}
        clearEndDateFilter={clearEndDateFilter}
        search={search}
        clearSearch={clearSearch}
        searchValue={state.searchValue}
      />
      {modalsState.viewCharity && currentCharity && (
        <CharityViewModal
          charity={currentCharity}
          closeModal={handleCloseModals('viewCharity')}
          showModal={modalsState.viewCharity}
          couponStatusType="client"
        />
      )}
      {modalsState.createCharity && (
        <CharityCreateModal
          closeModal={handleCloseModals('createCharity')}
          showModal={modalsState.createCharity}
          refresh={() =>
            getCharities(
              state.page,
              state.perPage,
              state.field,
              state.order,
              state.searchField,
              state.searchValue,
              state.typeFilter,
              state.statusFilter,
              state.startDate,
              state.endDate
            )
          }
          action="create"
        />
      )}
      {modalsState.editCharity && currentCharity && (
        <CharityCreateModal
          closeModal={handleCloseModals('editCharity')}
          showModal={modalsState.editCharity}
          charity={currentCharity}
          refresh={() =>
            getCharities(
              state.page,
              state.perPage,
              state.field,
              state.order,
              state.searchField,
              state.searchValue,
              state.typeFilter,
              state.statusFilter,
              state.startDate,
              state.endDate
            )
          }
          action="edit"
        />
      )}
      {modalsState.deleteCharity && currentCharity && (
        <Dialog
          fullWidth
          open={modalsState.deleteCharity}
          onClose={handleCloseModals('deleteCharity')}
        >
          <DialogContent>
            <RemoveEntityModalContent
              closeModal={handleCloseModals('deleteCharity')}
              removeUser={handleDeleteCoupon(currentCharity?._id!)}
              entityName="coupon"
            />
          </DialogContent>
        </Dialog>
      )}
      {modalsState.checkoutHide && currentCharity && (
        <Dialog
          fullWidth
          open={modalsState.checkoutHide}
          onClose={handleCloseModals('checkoutHide')}
        >
          <DialogContent>
            <ChangeEntityModalContent
              type={currentCharity.isActiveOnCheckoutPage ? 'hide' : 'show'}
              closeModal={handleCloseModals('checkoutHide')}
              changeStatus={() => {
                handleChangeCharityStatus(
                  currentCharity?._id!,
                  'checkout-page'
                );
              }}
              entityName={`${currentCharity.name} charity`}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
